import { ref, toValue, watch, type MaybeRefOrGetter } from "vue";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { ProductQtyBreak } from "~/layers/base/stores/productAttributes";
import { singleUnitPriceFromBreaks } from "~/templates/breadandboxers/domain/pricing";

/*
	A price is held for an hour, as long as the server keeps it. Longer, and a
	cart left open across a price change would strike a line through against a
	price no longer charged. A failure is held for a minute, so a product whose
	breaks will not load is not asked for again on every mount of the cart.
*/
const PRICE_TTL_MS = 60 * 60 * 1000;
const FAILURE_TTL_MS = 60 * 1000;

type CachedBreaks = { expiresAt: number; breaks: Promise<ProductQtyBreak[]> };

// Raw breaks per product and pricing context, shared by every line and every
// mount — the minicart and the checkout render the same lines, and two sizes of
// one product share one answer.
const breaksByProduct = new Map<string, CachedBreaks>();

/**
 * What one unit of a basket line's product costs on its own, read from its
 * quantity breaks.
 *
 * A line on a quantity break carries the break price in every price field, so
 * the basket alone cannot tell it was reduced. Only asked for from two units
 * up, where a break can apply — Norce counts a break per line, not across a
 * product's variants. The basket line carries no `HasQuantityBreaks`, so a
 * multi-unit line of a product without breaks still costs one request an hour.
 */
export const useLineSingleUnitPrice = (
	line: MaybeRefOrGetter<Partial<BasketItem>>
) => {
	const { listProductPriceQtyBreak } = useNorcePwa();
	const norceStore = useNorceStore();
	const cartStore = useCartStore();
	const singleUnitPrice = ref<number | null>(null);
	let latestRequest = 0;

	/*
		The store's pricing context prices the request; the basket's priced the
		line. They differ for a moment after a currency or market switch, until
		the basket is read back — comparing across them would set a SEK price
		against a EUR line. No saving until they agree.
	*/
	const basketMatchesStore = () => {
		const basket = cartStore.basket;
		const context = norceStore.storeContext;
		if (basket?.CurrencyId && basket.CurrencyId !== context.currencyId) {
			return false;
		}
		return !(
			basket?.SalesAreaId &&
			context.salesAreaId &&
			basket.SalesAreaId !== context.salesAreaId
		);
	};

	// Everything that prices the request, so the cache and the watcher go stale
	// together — `null` when the line cannot use a single-unit price.
	const productKey = () => {
		const item = toValue(line) ?? {};
		const canBeOnBreak =
			!!item.ProductId &&
			(item.Quantity ?? 0) > 1 &&
			(item.PriceDisplayIncVat ?? 0) > 0 &&
			item.ParentLineNo == null;
		if (import.meta.server || !canBeOnBreak || !basketMatchesStore()) {
			return null;
		}

		const context = norceStore.storeContext;
		return [
			context.pricelistSeed,
			context.salesAreaId,
			context.currencyId,
			item.ProductId
		].join("|");
	};

	const fetchBreaks = (key: string, productId: number) => {
		const context = norceStore.storeContext;
		// `id` first, as the product page sends it: the server cache keys on the
		// query string in insertion order. `currencyId` and `salesAreaId` are
		// typed as strings by `norce-pwa`; they only ever travel as query params.
		const breaks = listProductPriceQtyBreak({
			id: productId,
			...context,
			currencyId: String(context.currencyId),
			salesAreaId: context.salesAreaId?.toString()
		})
			// Typed as `{ Items }`, answered as a bare array — as the PDP reads it.
			.then((answer): ProductQtyBreak[] =>
				Array.isArray(answer) ? answer : []
			)
			.catch((error) => {
				breaksByProduct.set(key, {
					expiresAt: Date.now() + FAILURE_TTL_MS,
					breaks: Promise.resolve([])
				});
				console.error("Error loading listProductPriceQtyBreak:", error);
				return [];
			});

		breaksByProduct.set(key, { expiresAt: Date.now() + PRICE_TTL_MS, breaks });
		return breaks;
	};

	const loadBreaks = (key: string, productId: number) => {
		const cached = breaksByProduct.get(key);
		return cached && cached.expiresAt > Date.now()
			? cached.breaks
			: fetchBreaks(key, productId);
	};

	const lineKey = () => {
		const key = productKey();
		return key && `${key}|${toValue(line)?.PartNo ?? ""}`;
	};

	/*
		The basket is watched too, so the hour a price is held is measured from
		the cart actually being read back, not only from the line remounting — a
		checkout left open would otherwise keep a price past its hour. Inside the
		hour the cached breaks answer, so a basket update costs no request.
	*/
	watch(
		[lineKey, () => cartStore.basket],
		async ([currentKey], previous) => {
			const requestId = ++latestRequest;
			// Cleared when the line changes, so another product's price is never
			// shown meanwhile — not on a plain basket update, which would flicker.
			if (currentKey !== previous?.[0]) singleUnitPrice.value = null;
			const key = productKey();
			const { ProductId, PartNo } = toValue(line) ?? {};
			if (!currentKey || !key || !ProductId) {
				singleUnitPrice.value = null;
				return;
			}

			const breaks = await loadBreaks(key, ProductId);
			// Never another variant's breaks: priced differently, they would strike
			// the line through against a price it was never sold at.
			const forLine = PartNo
				? breaks.filter((entry) => entry.PartNo === PartNo)
				: breaks;
			// A newer line or quantity may have taken over while this one loaded.
			if (requestId === latestRequest) {
				singleUnitPrice.value = singleUnitPriceFromBreaks(forLine);
			}
		},
		{ immediate: true }
	);

	return { singleUnitPrice };
};
